/**
 * Estado de compliance del usuario, leído de forma tolerante.
 *
 * La respuesta de `GET /user/{email}` trae la MISMA columna
 * (`estado_compliance_id`) por dos caminos: la propiedad plana
 * `estadoComplianceID` y la relación `estadoCompliance.estadoID`. El front
 * quedó usando una u otra según el archivo — `ActivateGuard` mira la plana,
 * el home y remesas miraban la relación — así que bastaba con que la relación
 * no llegara para que el guard dejara entrar al cliente y la pantalla lo
 * mandara igual a "tu información está siendo validada".
 *
 * Este helper es el único lugar donde se resuelve. Prefiere la relación
 * (es la que el backend arma con el catálogo) y cae a la columna plana, que
 * es la que declara el modelo `User` y la que nunca falta.
 *
 * Devuelve `null` cuando no hay usuario o ninguno de los dos campos vino:
 * ausencia de dato NO es "por aprobar", y quien llama decide qué hacer.
 */
export function leerEstadoCompliance(user: any): number | null {
  const deRelacion = user?.estadoCompliance?.estadoID;
  if (typeof deRelacion === 'number') return deRelacion;

  const plano = user?.estadoComplianceID;
  if (typeof plano === 'number') return plano;

  return null;
}

/** Estado APROBADO del catálogo `estado_compliance`. */
export const ESTADO_COMPLIANCE_APROBADO = 3;

/**
 * Estado BLOQUEADO (4; antes SUSPENDIDO). Un cliente bloqueado opera igual que
 * uno aprobado; la única diferencia es que sus depósitos por transferencia
 * (cartola) no se acreditan solos: quedan retenidos para un operador. Los
 * abonos manuales sí los recibe.
 */
export const ESTADO_COMPLIANCE_BLOQUEADO = 4;

/** `true` si el usuario está aprobado por compliance. */
export function estaAprobadoPorCompliance(user: any): boolean {
  return leerEstadoCompliance(user) === ESTADO_COMPLIANCE_APROBADO;
}

/**
 * `true` si el usuario usa todo lo de un aprobado: aprobado (3) o bloqueado
 * (4). Es lo que habilitan las pantallas y guards de cliente; el pre-aprobado
 * con cupo se resuelve aparte (`puedeOperar` del cupo).
 */
export function operaComoAprobado(user: any): boolean {
  const estado = leerEstadoCompliance(user);
  return (
    estado === ESTADO_COMPLIANCE_APROBADO ||
    estado === ESTADO_COMPLIANCE_BLOQUEADO
  );
}
